import React, { FC } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import { GLOBALSETTINGS } from "../../../data/constants";

/** Kept in step with PostInterviewForm's own LIST_URL — the trail's "back to the grid" hop. */
const LIST_URL = "/hiring/postif";
const LIST_LABEL = "Post Interview Lists";

interface PifBreadcrumbsProps {
  /**
   * The final, non-navigable crumb: where the user is right now. Omit it on the list page itself —
   * "Post Interview Lists" then becomes the terminal crumb instead of a link to where you already are.
   */
  current?: string;
  /**
   * "onDark" for the list page, whose header is a full-bleed brown banner — the default inherited
   * colour is unreadable on it.
   */
  tone?: "default" | "onDark";
  /**
   * Set false on the emailed approver deep link (/sign/:token). That approver may hold no PostIF
   * menu permission at all, so the "Post Interview Lists" hop would land them on the
   * not-authorized page — the trail becomes Home → this form instead (Epic 79820).
   */
  showListCrumb?: boolean;
}

/**
 * Breadcrumb trail for the PostIF screens (list, form, approval review).
 *
 * Deliberately NOT `components/form/MyBreadcrumbs`, even though that is the app's breadcrumb
 * component: it renders the Redux `breadcrumbs` slice, which is the File Explorer's *folder* trail
 * (written by FolderTree / GlobalSearchBox, keyed by folder id) and is only mounted by PageDetail
 * on the /listDetails route. Driving it from here would mean writing these routes' crumbs into that
 * shared slice and leaving them behind for the file explorer to render later. This trail is fixed
 * and needs no store, so it stays presentational.
 *
 * It also has to live inside each screen rather than in the shared page-detail band, because the
 * shell skips that band for every /hiring/postif route (App.tsx's `hidePageDetail`) — these
 * screens bring their own headers.
 */
const PifBreadcrumbs: FC<PifBreadcrumbsProps> = ({
  current,
  tone = "default",
  showListCrumb = true,
}) => {
  const { search } = useLocation();
  const onDark = tone === "onDark";
  const linkColor = onDark ? "rgba(255, 255, 255, 0.75)" : "inherit";
  // MyBreadcrumbs leaves the terminal crumb on text.primary, which is unreadable on the banner —
  // white is the same intent (the crumb you're on, stronger than its links) against a dark ground.
  const currentColor = onDark ? "#fff" : "text.primary";
  // 15px on the banner is `.page-detail .breadcrumbs .primary`'s size; off the banner these sit on
  // white page content, which the shared band has no equivalent of, so they stay at body size.
  const currentSize = onDark ? "15px" : "inherit";

  return (
    // No separator prop: MUI's default "/" is what MyBreadcrumbs renders in the shared band.
    <Box sx={{ pt: onDark ? 0 : 2, pb: onDark ? 1 : 0, fontSize: 13, color: linkColor }}>
      <Breadcrumbs
        aria-label="breadcrumbs"
        sx={{ fontSize: "inherit", "& .MuiBreadcrumbs-separator": { color: linkColor } }}
      >
        <Link
          component={RouterLink}
          to={GLOBALSETTINGS.DEFAULT_PAGE}
          underline="hover"
          sx={{ color: linkColor }}
        >
          Home
        </Link>

        {!showListCrumb ? null : current ? (
          <Link
            component={RouterLink}
            to={{ pathname: LIST_URL, search }}
            underline="hover"
            sx={{ color: linkColor }}
          >
            {LIST_LABEL}
          </Link>
        ) : (
          <Typography sx={{ fontSize: currentSize, fontWeight: 600, color: currentColor }}>
            {LIST_LABEL}
          </Typography>
        )}

        {current && (
          <Typography sx={{ fontSize: currentSize, fontWeight: 600, color: currentColor }}>
            {current}
          </Typography>
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default PifBreadcrumbs;
